import React, { useState, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { TaskRow } from '../components/TaskRow.jsx';
import { todayStr, addDays, formatDate } from '../lib/dates.js';

export function TasksPage({ theme, profile, partner, tasks, initialTab = 'all', onAddTask, onComplete, onDelete, onEdit }) {
  const [activeTab, setActiveTab] = useState(initialTab);

  // If parent changes initialTab (e.g. user clicked "Vis alle X" on dashboard), update active tab
  React.useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  const tabs = useMemo(() => {
    const fromPartner = tasks.filter(t => t.list === 'sent' && t.assignedTo === profile?.id);
    const shared = tasks.filter(t => t.list === 'shared');
    const myPrivate = tasks.filter(t => t.list === 'private' && t.createdBy === profile?.id);
    const sentOut = tasks.filter(t => t.list === 'sent' && t.createdBy === profile?.id && t.assignedTo !== profile?.id);

    return [
      { id: 'all', label: 'Alle', count: tasks.length, items: tasks },
      partner && { id: 'received', label: `Opgaver fra ${partner.name?.split(' ')[0]}`, count: fromPartner.length, items: fromPartner, accent: theme.coral },
      partner && { id: 'shared', label: 'Fælles opgaver', count: shared.length, items: shared, accent: theme.teal },
      { id: 'private', label: 'Private opgaver', count: myPrivate.length, items: myPrivate },
      partner && { id: 'sent', label: `Sendt til ${partner.name?.split(' ')[0]}`, count: sentOut.length, items: sentOut, accent: theme.gold },
    ].filter(Boolean);
  }, [tasks, profile, partner, theme]);

  const activeTabData = tabs.find(t => t.id === activeTab) || tabs[0];
  const visibleTasks = activeTabData.items;

  // Group by deadline urgency
  const grouped = useMemo(() => {
    const today = todayStr();
    const tomorrow = addDays(today, 1);
    const weekEnd = addDays(today, 7);

    const groups = {
      overdue: [],
      today: [],
      tomorrow: [],
      thisWeek: [],
      later: [],
      noDate: [],
    };

    for (const t of visibleTasks) {
      if (!t.deadline) {
        groups.noDate.push(t);
        continue;
      }
      const date = t.deadline.split('T')[0];
      if (date < today) groups.overdue.push(t);
      else if (date === today) groups.today.push(t);
      else if (date === tomorrow) groups.tomorrow.push(t);
      else if (date <= weekEnd) groups.thisWeek.push(t);
      else groups.later.push(t);
    }

    return groups;
  }, [visibleTasks]);

  return (
    <div>
      <h1 style={{ margin: '0 0 16px', fontSize: 22, fontWeight: 700 }}>Opgaver</h1>

      {/* Quick action */}
      <button onClick={() => onAddTask('mine')} style={{
        background: theme.coral, color: 'white',
        border: 'none', borderRadius: 10,
        padding: '12px 14px', width: '100%',
        fontSize: 14, fontWeight: 600, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        marginBottom: 16,
      }}>
        <Plus size={16} /> Opret opgave
      </button>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: 6,
        overflowX: 'auto',
        marginBottom: 16,
        paddingBottom: 4,
        scrollbarWidth: 'thin',
      }}>
        {tabs.map(t => {
          const active = activeTab === t.id;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              background: active ? (t.accent || theme.text) : theme.bgCard,
              color: active ? (t.accent ? 'white' : theme.bg) : theme.textMuted,
              border: `1px solid ${active ? (t.accent || theme.text) : theme.borderSubtle}`,
              padding: '7px 12px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}>
              {t.label}
              {t.count > 0 && (
                <span style={{
                  marginLeft: 6, fontSize: 10,
                  opacity: 0.7,
                }}>{t.count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Empty state */}
      {visibleTasks.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '40px 20px',
          background: theme.bgCard,
          border: `1px solid ${theme.borderSubtle}`,
          borderRadius: 12,
          color: theme.textMuted,
          fontSize: 13,
        }}>
          Ingen opgaver i denne kategori.
        </div>
      )}

      {/* Groups */}
      {visibleTasks.length > 0 && (
        <>
          <Group theme={theme} title="Forsinket" tasks={grouped.overdue} tone="error"
            currentUserId={profile?.id} partner={partner} onComplete={onComplete} onDelete={onDelete} onEdit={onEdit} />
          <Group theme={theme} title="I dag" tasks={grouped.today} tone="primary"
            currentUserId={profile?.id} partner={partner} onComplete={onComplete} onDelete={onDelete} onEdit={onEdit} />
          <Group theme={theme} title="I morgen" tasks={grouped.tomorrow}
            currentUserId={profile?.id} partner={partner} onComplete={onComplete} onDelete={onDelete} onEdit={onEdit} />
          <Group theme={theme} title="Denne uge" tasks={grouped.thisWeek}
            currentUserId={profile?.id} partner={partner} onComplete={onComplete} onDelete={onDelete} onEdit={onEdit} />
          <Group theme={theme} title="Senere" tasks={grouped.later}
            currentUserId={profile?.id} partner={partner} onComplete={onComplete} onDelete={onDelete} onEdit={onEdit} />
          <Group theme={theme} title="Uden dato" tasks={grouped.noDate}
            currentUserId={profile?.id} partner={partner} onComplete={onComplete} onDelete={onDelete} onEdit={onEdit} />
        </>
      )}
    </div>
  );
}

function Group({ theme, title, tasks, tone, currentUserId, partner, onComplete, onDelete, onEdit }) {
  if (tasks.length === 0) return null;
  const color = tone === 'error' ? '#EF4444' : tone === 'primary' ? theme.coral : theme.textMuted;

  return (
    <div style={{ marginBottom: 16 }}>
      <h4 style={{
        margin: '0 0 8px', fontSize: 11, fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: 1,
        color,
      }}>{title} ({tasks.length})</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {tasks.map(t => (
          <TaskRow key={t.id} theme={theme} task={t} currentUserId={currentUserId} partner={partner}
            onComplete={onComplete} onDelete={onDelete} onEdit={onEdit} />
        ))}
      </div>
    </div>
  );
}
